/**
 * وب در برابر موبایل — تکه‌های مشترکِ سربرگِ فعالیت (WHOSUP-242).
 *
 * ═══ چرا «هم روی هم و هم جدا» و نه یکی از آن دو ═══
 *
 * سوال دو تاست و یک نمایش هر دو را جواب نمی‌دهد:
 *
 *   «کدام بزرگ‌تر است»  →  باید کنارِ هم باشند. جدولِ مقایسه و نمودارِ
 *                          روزانه همین‌اند: دو سری روی یک محور.
 *   «داخلِ اپ چه گذشت»  →  باید فقط یکی باشد. قاطیِ دو سطح، «میانه‌ی مدتِ
 *                          نشست» میانه‌ی هیچ‌کس نیست.
 *
 * پس پیش‌فرض روی‌هم است و یک فیلترِ بالای صفحه هر وقت لازم شد جداشان
 * می‌کند. فیلتر در **آدرس** می‌نشیند (`?surface=mobile`) تا رفرش نپراندش
 * و بشود لینکش را فرستاد — همان قاعده‌ی `useUrlState`.
 *
 * ⚠ رنگ هیچ‌جا تنها حاملِ معنی نیست؛ توضیحش سرِ `.swatch` در `base.css`.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { opsRepo } from '@/core/data/opsRepo';
import type { AppActivity, SurfaceKey } from '@/core/data/opsRepo';
import {
  FUNNEL_FA, FUNNEL_KEY_FA, FUNNEL_LEAK_BELOW, FUNNEL_VERDICT_FA, FUNNEL_WEAK_BELOW,
  cardsFor, dayPeak, dayRows, funnelDetails, funnelSteps, lostScreens, oneSidedEvents, retentionRows, peopleMix, surfaceCards, surfaceShare,
  type DayRow, type RetentionRate, type SurfaceFilter,
} from '@/core/lib/activityReport';
import { Two, fmtNum as fmt } from '@/ui/Num';

/**
 * ستونِ دومِ این سربرگ. تنها جای اپ که `RUN_LABEL` نیست: این جدول را کاربر
 * پر می‌کند نه کرالر، پس «آخرین اجرا» برایش معنی ندارد.
 */
export const DAY = '۲۴ ساعت';

export const SURFACE_FA: Record<SurfaceKey, string> = { web: 'وب', mobile: 'موبایل' };

const FILTERS: { id: SurfaceFilter; label: string }[] = [
  { id: 'all', label: 'هر دو' },
  { id: 'web', label: 'وب' },
  { id: 'mobile', label: 'موبایل' },
];

const pct = (x: number): string => `${Math.round(x * 100)}٪`;

/** روزِ `2026-09-19` → `09-19`. سال در ده روز عوض نمی‌شود و جا تنگ است. */
const dayLabel = (d: string): string => d.slice(5);

/* ─────────────────────────── فیلتر ─────────────────────────── */

export function SurfaceChips({ value, onChange }: {
  value: SurfaceFilter; onChange: (v: SurfaceFilter) => void;
}) {
  return (
    <div className="chiprow">
      <span className="dim">سطح:</span>
      {FILTERS.map((f) => (
        <button key={f.id} type="button" className={`chip${value === f.id ? ' on' : ''}`}
          onClick={() => onChange(f.id)}>
          {f.id === 'all' ? null : <span className={`swatch ${f.id}`} />}
          {f.label}
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────── جدولِ مقایسه ─────────────────────── */

/**
 * چهار عددِ بالای صفحه، این بار برای هر سطح جدا و کنارِ هم.
 *
 * ⚠ فقط در حالتِ «هر دو» نشان داده می‌شود: زیرِ فیلترِ یک سطح، همین اعداد
 * بالای صفحه در کارت‌ها هستند و تکرارشان فقط صفحه را بلند می‌کند.
 */
export function SurfaceCompare({ a }: { a: AppActivity }) {
  const rows = surfaceCards(a);
  const share = surfaceShare(a);
  const mix = peopleMix(a, 'd10');
  const all = cardsFor(a, 'all');
  const known = mix.web_only + mix.mobile_only + mix.both;

  return (
    <div className="card">
      <h2>وب در برابر موبایل</h2>
      <div className="scrollx">
        <table className="tbl">
          <thead>
            <tr>
              <th>سطح</th>
              <th className="num">رویداد</th>
              <th className="num">نشست</th>
              <th className="num">کاربرِ واردشده</th>
              <th className="num">مهمان</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.surface}>
                <td><span className={`swatch ${r.surface}`} />{SURFACE_FA[r.surface]}</td>
                <td className="num"><Two v={r.v.total} runLabel={DAY} /></td>
                <td className="num"><Two v={r.v.sessions} runLabel={DAY} /></td>
                <td className="num"><Two v={r.v.people} runLabel={DAY} /></td>
                <td className="num"><Two v={r.v.guests} runLabel={DAY} /></td>
              </tr>
            ))}
            <tr>
              <td className="dim">هر دو</td>
              <td className="num"><Two v={all.total} runLabel={DAY} /></td>
              <td className="num"><Two v={all.sessions} runLabel={DAY} /></td>
              <td className="num"><Two v={all.people} runLabel={DAY} /></td>
              <td className="num"><Two v={all.guests} runLabel={DAY} /></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* سهمِ ده‌روزه‌ی هر سطح از رویدادها — یک میله، چون یک نسبت است. */}
      <div className="sharebar" title={`وب ${pct(share.web)} · موبایل ${pct(share.mobile)}`}>
        {/* عرض از داده می‌آید، پس این دو تنها جای این فایل‌اند که استایلِ
            درون‌خطی دارند — رنگ هم همین‌جا می‌ماند تا کنارِ عرضش باشد. */}
        <span style={{ width: `${share.web * 100}%`, background: 'var(--brand)' }} />
        <span style={{ width: `${share.mobile * 100}%`, background: 'var(--accent)' }} />
      </div>
      <p className="note">
        <span className="swatch web" />وب {pct(share.web)}
        {' · '}
        <span className="swatch mobile" />موبایل {pct(share.mobile)}
        {' — '}از رویدادهای ده روزِ اخیر.
      </p>

      {/* ═══ «کجا هردو» ═══ */}
      <p className="note">
        {known ? (
          <>
            از {fmt(known)} کاربرِ واردشده‌ی این ده روز،{' '}
            <b>{fmt(mix.web_only)}</b> فقط سایت را دیدند،{' '}
            <b>{fmt(mix.mobile_only)}</b> فقط اپ را، و <b>{fmt(mix.both)}</b> هر دو را.
          </>
        ) : 'در این ده روز هیچ کاربرِ واردشده‌ای ثبت نشده.'}
        {' '}⚠ به همین دلیل جمعِ دو ردیفِ «کاربرِ واردشده» از ردیفِ «هر دو»
        بیشتر می‌شود و این باگ نیست: کسی که هر دو را باز کرده در هر دو
        شمرده می‌شود. مهمان‌ها در این تقسیم نیستند، چون شناسه‌ای ندارند که
        بشود نشستِ سایت و نشستِ اپشان را یکی دانست.
      </p>
    </div>
  );
}

/* ─────────────────────── نمودارِ روزها ─────────────────────── */

/**
 * رویدادهای هر روز، دو سری روی هم.
 *
 * `by_day` از مایگریشنِ 0068 در جواب بود و **هیچ‌وقت کشیده نمی‌شد**. حالا
 * که هر روز دو عدد دارد، نکشیدنش یعنی تنها جایی که «کِی کدام سطح شلوغ
 * بود» دیده می‌شود از بین برود.
 *
 * ⚠ بی کتابخانه — همان تصمیمِ `Bars` در `Journey.tsx`: دو `span` با ارتفاعِ
 * درصدی همان کار را می‌کنند و وابستگیِ تازه نمی‌خواهند.
 */
export function DayBars({ a, filter }: { a: AppActivity; filter: SurfaceFilter }) {
  const rows = dayRows(a);
  const peak = dayPeak(rows, filter);

  if (!rows.length) {
    return (
      <div className="card">
        <h2>رویداد در روز</h2>
        <p className="dim">—</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>رویداد در روز</h2>
      <div className="daybars">
        {rows.map((r) => <Col key={r.day} r={r} peak={peak} filter={filter} />)}
      </div>
      <div className="dayaxis">
        {rows.map((r) => <span key={r.day} className="ltr">{dayLabel(r.day)}</span>)}
      </div>
      <p className="note">
        {filter === 'all' ? (
          <>
            <span className="swatch web" />وب
            {' · '}
            <span className="swatch mobile" />موبایل
            {' — '}هر ستون یک روز، و بلندی‌اش تعدادِ رویدادِ آن روز. ماوس را
            روی ستون نگه دار تا عددها بیایند.
          </>
        ) : (
          <>
            فقط <b>{SURFACE_FA[filter]}</b>. بلندیِ ستون‌ها نسبت به بلندترین
            ستونِ <b>همین</b> سطح است، پس با نمودارِ سطحِ دیگر مستقیم مقایسه
            نمی‌شود — برای مقایسه فیلتر را روی «هر دو» بگذار.
          </>
        )}
        {' '}خطِ نازک یعنی آن روز هیچ رویدادی نبود.
      </p>
    </div>
  );
}

function Col({ r, peak, filter }: { r: DayRow; peak: number; filter: SurfaceFilter }) {
  const web = filter === 'mobile' ? 0 : r.web;
  const mobile = filter === 'web' ? 0 : r.mobile;
  const title = `${r.day} — وب ${fmt(r.web)} · موبایل ${fmt(r.mobile)}`
    + ` · نشست ${fmt(r.sessionsWeb + r.sessionsMobile)}`;
  const h = (n: number): string => `${(n / peak) * 100}%`;

  return (
    <span className="col" title={title}>
      {/* گوشه‌ی گرد فقط روی تکه‌ی بالایی، وگرنه وسطِ میله بریده به نظر می‌رسد. */}
      {mobile > 0 ? (
        <span className="seg mobile top" style={{ height: h(mobile) }} />
      ) : null}
      {web > 0 ? (
        <span className={`seg web${mobile > 0 ? '' : ' top'}`} style={{ height: h(web) }} />
      ) : null}
      {web + mobile === 0 ? <span className="none" /> : null}
    </span>
  );
}

/* ─────────────────────────── قیف ─────────────────────────── */

/** بازه‌های قیف — روز. */
const FUNNEL_DAYS = [7, 10, 30, 90] as const;

/**
 * کارتِ قیف (WHOSUP-292): آدم‌های یکتا در هر قدم، از `admin_funnel`.
 *
 * داده‌اش را خودش می‌گیرد تا مایگریشنِ نرسیده (0161) فقط همین کارت را
 * خالی کند. در نگاهِ اول فقط میله‌ها و درصدها؛ منبع و صفحه‌ی ورود زیرِ «بیشتر».
 *
 * ⚠ قدمِ صفر «هنوز ثبت نمی‌شود» است و نه ۰٪ — صفر شاید فقط یعنی نسخه‌ی
 * فرستنده هنوز به کسی نرسیده.
 */
export function FunnelChart({ filter }: { filter: SurfaceFilter }) {
  const [days, setDays] = React.useState<number>(10);
  const [more, setMore] = React.useState(false);
  const q = useQuery({ queryKey: ['admin', 'funnel', days], queryFn: () => opsRepo.funnel(days) });
  const steps = funnelSteps(q.data, filter);

  return (
    <div className="card">
      <h2>قیف{filter === 'all' ? '' : ` — ${SURFACE_FA[filter]}`}</h2>
      <div className="chiprow" style={{ marginBottom: 8 }}>
        {FUNNEL_DAYS.map((d) => (
          <button key={d} type="button" className={`chip${d === days ? ' on' : ''}`}
            onClick={() => setDays(d)}>{fmt(d)} روز</button>
        ))}
      </div>

      {q.isLoading ? <p className="dim">در حالِ خواندن…</p> : null}
      {q.error ? (
        <p className="note">
          قیف خوانده نشد — شاید مایگریشنِ <span className="mono">0161</span> روی این
          دیتابیس اجرا نشده. ({(q.error as Error).message})
        </p>
      ) : null}

      {q.data ? (
        // نمودارِ ستونی: هر قدم یک ستون، بلندی نسبت به پرجمعیت‌ترین قدم.
        <div className="funnelchart">
          {steps.map((s) => (
            <div key={s.name} className="fcol" title={`${s.name} — ${fmt(s.people)} آدم`}>
              <span className="fval">
                {s.recorded ? fmt(s.people) : <span className="dim">هنوز ثبت نمی‌شود</span>}
              </span>
              <span className="fbarwrap">
                {s.recorded
                  ? <span className="fbar" style={{ height: `${Math.max(s.width * 100, 2)}%` }} />
                  : <span className="fnone" />}
              </span>
              <span className="flabel">{FUNNEL_FA[s.name]}</span>
              <span className="num dim">
                {s.ofPrev === null ? ' ' : pct(s.ofPrev)}
                {s.verdict && s.verdict !== 'ok' ? ` · ${FUNNEL_VERDICT_FA[s.verdict]}` : ''}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      <p className="note">
        آدم‌های یکتا در {fmt(days)} روزِ اخیر، بی حساب‌های داخلی. ⚠ اپِ موبایلِ
        تست‌کننده‌ها تا بیلدِ بعدی (<span className="mono">versionCode 3</span>) این
        قدم‌ها را نمی‌فرستد.
      </p>

      {q.data ? (
        <>
          <button type="button" className="linkbtn" onClick={() => setMore((m) => !m)}>
            {more ? 'کمتر' : 'بیشتر'}
          </button>
          {more ? (
            <div style={{ marginTop: 8 }}>
              <div className="scrollx">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>قدم</th>
                      <th className="num">آدم</th>
                      <th className="num"><span className="swatch web" />وب</th>
                      <th className="num"><span className="swatch mobile" />موبایل</th>
                      <th className="num">نسبت به قبلی</th>
                    </tr>
                  </thead>
                  <tbody>
                    {steps.map((s) => (
                      <tr key={s.name}>
                        <td>{FUNNEL_FA[s.name]} <span className="mono dim">{s.name}</span></td>
                        <td className="num">{s.recorded ? fmt(s.people) : 'هنوز ثبت نمی‌شود'}</td>
                        <td className="num">{fmt(s.web)}</td>
                        <td className="num">{fmt(s.mobile)}</td>
                        <td className="num">
                          {s.ofPrev === null || !s.verdict
                            ? '—' : `${pct(s.ofPrev)} · ${FUNNEL_VERDICT_FA[s.verdict]}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {steps.filter((s) => s.recorded).map((s) => {
                const rows = funnelDetails(q.data, s.name, filter);
                return rows.length ? (
                  <div key={s.name} className="scrollx" style={{ marginTop: 8 }}>
                    <h3>{FUNNEL_FA[s.name]}</h3>
                    <table className="tbl">
                      <tbody>
                        {rows.map((r) => (
                          <tr key={`${r.key}:${r.value}`}>
                            <td>{FUNNEL_KEY_FA[r.key] ?? r.key}</td>
                            <td className="mono">{r.value}</td>
                            <td className="num">{fmt(r.people)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null;
              })}
              <ul className="note" style={{ paddingInlineStart: 18 }}>
                <li>
                  «آدم» یعنی کاربرِ واردشده، یا نشستِ مهمان. بلندیِ هر ستون نسبت به
                  پرجمعیت‌ترین قدم است و عددِ کم‌رنگ زیرش، نسبت به قدمِ قبلی.
                </li>
                <li>
                  این قیفِ «سخت» نیست: کسی که از فید یا کشف آمده هم رویداد باز
                  می‌کند، پس قدمِ دوم می‌تواند از اول بزرگ‌تر باشد و «نسبت به قبلی»
                  از ۱۰۰٪ بگذرد.
                </li>
                <li>
                  حکم: زیرِ {pct(FUNNEL_LEAK_BELOW)} «ریزشِ شدید»، زیرِ {pct(FUNNEL_WEAK_BELOW)} «ضعیف».
                </li>
                <li>
                  کسی که هم روی وب و هم روی موبایل بوده در هر دو ستون هست؛ برای همین
                  جمعِ وب و موبایل (و عددهای جزئیات زیرِ «هر دو») می‌تواند از کل بیشتر شود.
                </li>
                <li>«پاسخ / علاقه» هم پاسخ به دعوت است و هم نشان کردن (قلب) در کشف.</li>
              </ul>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/* ─────────────────── چه چیزی فقط یک‌جا ثبت می‌شود ─────────────────── */

/**
 * ناهم‌ترازی‌هایی که از **کد** می‌آیند و نه از داده.
 *
 * فهرستِ زیرِ آن (رویدادهای یک‌طرفه) از خودِ اعداد درمی‌آید و خودش را
 * به‌روز می‌کند. ولی سه چیز هست که هرگز در `app_events` دیده نمی‌شوند و
 * اگر نوشته نشوند، هر مقایسه‌ای در این صفحه بی‌صدا غلط خوانده می‌شود.
 *
 * این‌ها این‌جا **گزارش** می‌شوند و در همین تیکت درست نمی‌شوند: هر کدام
 * کارِ خودش را دارد و قاطی کردنشان با این تغییر یعنی همان «تغییرِ
 * نامرتبط» که قاعده‌های مخزن منعش می‌کنند.
 */
const KNOWN_GAPS: { what: string; why: string }[] = [
  {
    what: 'برچسبِ دکمه‌ها یک واژگان نیست',
    why: 'موبایل برچسبِ دستیِ داخلِ کد را می‌فرستد (`filter:price`) و وب متنِ '
      + 'دکمه را به کلیدِ ترجمه‌اش برمی‌گرداند. پس یک دکمه‌ی یکسان دو نامِ '
      + 'متفاوت می‌گیرد و جدولِ «روی هر صفحه چه زدند» بینِ دو سطح جمع‌پذیر نیست.',
  },
  {
    what: 'دکمه‌های بیرون از کیت فقط روی وب خودکار شمرده می‌شوند',
    why: 'وب یک شنونده‌ی سراسریِ کلیک دارد (`trackDomTap`) که هر دکمه‌ای را '
      + 'می‌گیرد؛ موبایل چنین چیزی ندارد و فقط دکمه‌هایی شمرده می‌شوند که '
      + 'خودشان `tapped()` را صدا زده باشند. یعنی «لمس»ِ موبایل کف است، نه کل.',
  },
  {
    what: '«از کجا آمد» روی دو سطح دو معنی دارد',
    why: 'ارجاع از سایتِ دیگر فقط در مرورگر وجود دارد و لینکِ عمیقِ اپ فقط در '
      + 'موبایل. پس خالی بودنِ «از سایتِ دیگر» روی موبایل یافته نیست. '
      + 'قیفِ قبل از باز شدنِ سایت هم در دیتابیسِ ما نیست — در کلادفلر است، و '
      + 'قرینه‌اش برای اپ در کنسولِ فروشگاه.',
  },
];

export function OneSided({ a }: { a: AppActivity }) {
  const rows = oneSidedEvents(a);

  return (
    <div className="card">
      <h2>چه چیزی فقط یک‌جا ثبت می‌شود</h2>
      <p className="note">
        رویدادی که در ده روزِ اخیر فقط از یک سطح آمده. دو چیزِ کاملاً متفاوت
        به این فهرست می‌رسند و از روی عدد نمی‌شود فرقشان را گفت: رویدادی که
        آن‌طرف اصلاً معنی ندارد، و رویدادی که آن‌طرف هم باید ثبت می‌شد و
        نشده. دومی یک کارِ ناتمام است.
      </p>
      {rows.length === 0 ? (
        <p className="dim">هیچ رویدادی یک‌طرفه نیست — هر چه ثبت می‌شود از هر دو سطح می‌آید.</p>
      ) : (
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr><th>رویداد</th><th>فقط از</th><th className="num">تعداد</th><th className="num">نشست</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name}>
                  <td className="mono">{r.name}</td>
                  <td>
                    <span className={`swatch ${r.side === 'web' ? 'web' : 'mobile'}`} />
                    {SURFACE_FA[r.side === 'web' ? 'web' : 'mobile']}
                  </td>
                  <td className="num">{fmt(r.side === 'web' ? r.web : r.mobile)}</td>
                  <td className="num">{fmt(r.sessions)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 style={{ margin: '14px 0 0', fontSize: 13 }}>
        و سه چیزی که اصلاً به این جدول نمی‌رسند
      </h3>
      <ul className="note" style={{ paddingInlineStart: 18 }}>
        {KNOWN_GAPS.map((g) => (
          <li key={g.what} style={{ marginTop: 4 }}>
            <b>{g.what}</b> — {g.why}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─────────────────────────── برگشت (WHOSUP-310) ─────────────────────────── */

const RetentionCell = ({ r }: { r: RetentionRate }) =>
  r.kind === 'early' ? <span className="dim">هنوز زود است</span>
    : r.kind === 'none' ? <span className="dim">—</span>
      : <span title={`${fmt(r.back)} از ${fmt(r.of)}`}>{pct(r.rate)}</span>;

/**
 * برگشتِ تازه‌واردها، هفته‌به‌هفته — مکملِ قیف: قیف می‌گوید در یک بازدید تا
 * کجا رفتند، این می‌گوید دوباره آمدند یا نه. تابعِ خودش را دارد
 * (`admin_retention`، مایگریشن 0162) تا نرسیدنش فقط همین کارت را بشکند.
 */
export function RetentionCard({ filter }: { filter: SurfaceFilter }) {
  const [more, setMore] = React.useState(false);
  const q = useQuery({ queryKey: ['admin', 'retention'], queryFn: () => opsRepo.retention(12) });
  const rows = retentionRows(q.data, filter);
  const lost = lostScreens(q.data, filter);

  return (
    <div className="card">
      <h2>برگشتِ تازه‌واردها{filter === 'all' ? '' : ` — ${SURFACE_FA[filter]}`}</h2>

      {q.isLoading ? <p className="dim">در حالِ خواندن…</p> : null}
      {q.error ? (
        <p className="note">
          برگشت خوانده نشد — شاید مایگریشنِ <span className="mono">0162</span> روی این
          دیتابیس اجرا نشده. ({(q.error as Error).message})
        </p>
      ) : null}

      {q.data && rows.length === 0 ? (
        <p className="dim">در {fmt(q.data.weeks)} هفته‌ی اخیر هیچ تازه‌واردِ واردشده‌ای نبوده.</p>
      ) : null}

      {rows.length ? (
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                <th>هفته‌ی اولین نشست</th>
                <th className="num">تازه‌وارد</th>
                <th className="num">روزِ بعد</th>
                <th className="num">هفته‌ی بعد</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.week}>
                  <td className="mono">{r.week}</td>
                  <td className="num">{fmt(r.newcomers)}</td>
                  <td className="num"><RetentionCell r={r.nextDay} /></td>
                  <td className="num"><RetentionCell r={r.nextWeek} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <ul className="note" style={{ paddingInlineStart: 18 }}>
        <li>
          <b>فقط کاربرهای واردشده.</b> مهمان در رویدادها شناسه‌ی ثابتی ندارد (هر باز
          شدن یک نشستِ تازه است)، پس برگشتنش این‌جا شمرده نمی‌شود.
        </li>
        <li>
          هر نفر یک بار، در هفته‌ی اولین نشستش (روی همین سطح) شمرده می‌شود. «روزِ بعد» =
          ۲۴ تا ۴۸ ساعت بعد از اولین رویداد؛ «هفته‌ی بعد» = روزِ ۲ تا ۸. «هنوز زود است»
          یعنی کسی از آن هفته هنوز پنجره‌اش تمام نشده.
        </li>
        <li>حساب‌های داخلی شمرده نمی‌شوند. فقط ۱۲ هفته‌ی اخیر خوانده می‌شود.</li>
        <li>
          ⚠ درستیِ این عددها به شمارشِ درستِ نشست‌ها بستگی دارد
          (<span className="mono">WHOSUP-295</span>).
        </li>
      </ul>

      {q.data ? (
        <>
          <button type="button" className="linkbtn" onClick={() => setMore((m) => !m)}>
            {more ? 'کمتر' : 'بیشتر'}
          </button>
          {more ? (
            <div className="scrollx" style={{ marginTop: 8 }}>
              <h3>آخرین صفحه‌ای که برنگشته‌ها دیدند</h3>
              {lost.length ? (
                <table className="tbl">
                  <tbody>
                    {lost.map((l) => (
                      <tr key={l.screen ?? ''}>
                        <td className="mono">{l.screen ?? <span className="dim">ثبت نشده</span>}</td>
                        <td className="num">{fmt(l.people)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="dim">هنوز کسی نیست که پنجره‌ی هشت‌روزه‌اش تمام شده باشد و برنگشته باشد.</p>}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
